import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProgressTracking = ({ plan, getStatusColor }) => {
  const completedProcedures = plan?.phases?.flatMap(phase => phase?.procedures)?.filter(proc => proc?.status === 'completed')?.length || 0;
  const totalProcedures = plan?.phases?.flatMap(phase => phase?.procedures)?.length || 0;
  const procedureCompletionRate = totalProcedures > 0 ? Math.round((completedProcedures / totalProcedures) * 100) : 0;

  const completedPhases = plan?.phases?.filter(phase => phase?.status === 'completed')?.length || 0;
  const totalPhases = plan?.phases?.length || 0;
  const phaseCompletionRate = totalPhases > 0 ? Math.round((completedPhases / totalPhases) * 100) : 0;

  const beforeAfterComparisons = [
    {
      id: 1,
      procedure: 'Composite Filling - Tooth #3',
      beforeImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_dental_xray_2.png',
      beforeAlt: 'X-ray showing caries on tooth #3 before treatment',
      afterImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_intraoral_restoration.png',
      afterAlt: 'Intraoral photo showing completed composite restoration',
      date: '2026-01-20',
      outcome: 'Excellent restoration with proper marginal seal. Patient reports no sensitivity.'
    },
    {
      id: 2,
      procedure: 'Crown Preparation - Tooth #14',
      beforeImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_dental_xray_1.png',
      beforeAlt: 'Bitewing X-ray showing tooth #14 before crown preparation',
      afterImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_crown_prep.png',
      afterAlt: 'Intraoral photo of prepared tooth ready for crown',
      date: '2026-02-05',
      outcome: 'Crown preparation completed. Temporary crown placed. Final crown scheduled for 2 weeks.'
    }
  ];

  const outcomeMetrics = [
    { label: 'Patient Satisfaction', value: '95%', status: 'excellent', icon: 'ThumbsUp' },
    { label: 'Treatment Adherence', value: '100%', status: 'excellent', icon: 'CheckCircle' },
    { label: 'Complication Rate', value: '0%', status: 'excellent', icon: 'Shield' },
    { label: 'On-Time Completion', value: '85%', status: 'good', icon: 'Clock' }
  ];

  return (
    <div className="space-y-6">
      {/* Overall Progress */}
      <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="TrendingUp" size={20} className="text-primary" />
          Treatment Progress Overview
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Phase Progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-foreground">Phase Completion</p>
              <span className="text-sm font-semibold text-primary">{phaseCompletionRate}%</span>
            </div>
            <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${phaseCompletionRate}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {completedPhases} of {totalPhases} phases completed
            </p>
          </div>

          {/* Procedure Progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-foreground">Procedure Completion</p>
              <span className="text-sm font-semibold text-success">{procedureCompletionRate}%</span>
            </div>
            <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-success transition-all duration-500"
                style={{ width: `${procedureCompletionRate}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {completedProcedures} of {totalProcedures} procedures completed
            </p>
          </div>
        </div>

        {/* Overall Completion */}
        <div className="mt-6 p-4 bg-primary/5 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-base font-semibold text-foreground">Overall Treatment Progress</p>
            <span className="text-2xl font-bold text-primary">{plan?.completionPercentage}%</span>
          </div>
          <div className="w-full h-4 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-success transition-all duration-500"
              style={{ width: `${plan?.completionPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Outcome Metrics */}
      <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="BarChart3" size={20} className="text-primary" />
          Outcome Measurements
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {outcomeMetrics?.map((metric, idx) => (
            <div key={idx} className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={metric?.icon} size={16} className="text-primary" />
                <p className="text-xs font-medium text-muted-foreground">{metric?.label}</p>
              </div>
              <p className="text-2xl font-bold text-foreground">{metric?.value}</p>
              <div className="flex items-center gap-1 mt-2">
                <Icon name="TrendingUp" size={12} className="text-success" />
                <span className="text-xs text-success">Excellent</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Before/After Comparisons */}
      <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Image" size={20} className="text-primary" />
          Before & After Documentation
        </h3>

        <div className="space-y-6">
          {beforeAfterComparisons?.map((comparison) => (
            <div key={comparison?.id} className="bg-background rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">{comparison?.procedure}</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                {/* Before Image */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Before Treatment</p>
                  <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                    <img
                      src={comparison?.beforeImage}
                      alt={comparison?.beforeAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* After Image */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">After Treatment</p>
                  <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                    <img
                      src={comparison?.afterImage}
                      alt={comparison?.afterAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="FileText" size={14} className="text-primary" />
                  <p className="text-xs font-medium text-foreground">Clinical Outcome</p>
                </div>
                <p className="text-xs text-muted-foreground">{comparison?.outcome}</p>
                <p className="text-xs text-muted-foreground mt-1">Completed: {comparison?.date}</p>
              </div>

              <div className="flex items-center gap-2 mt-3">
                <Button variant="outline" size="xs" iconName="Download">
                  Export
                </Button>
                <Button variant="outline" size="xs" iconName="Share2">
                  Share with Patient
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quality Assurance Notes */}
      <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Shield" size={20} className="text-primary" />
          Quality Assurance
        </h3>

        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-success/10 rounded-lg">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">All procedures meet quality standards</p>
              <p className="text-xs text-muted-foreground mt-1">Last review: 2026-01-18</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-success/10 rounded-lg">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Patient consent documentation complete</p>
              <p className="text-xs text-muted-foreground mt-1">All required forms signed and filed</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-success/10 rounded-lg">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Imaging documentation adequate</p>
              <p className="text-xs text-muted-foreground mt-1">All required radiographs and photos on file</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressTracking;